import "@event-calendar/core/index.css";
import React, { useEffect, useMemo, useState } from "react";
import { useLoaderData, useRevalidator } from "react-router";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import * as d from "shared/src/date.mjs";
import * as api from "../services/api";
import * as ws from "../utils/ws";
import {
  currentLocationFromRequest,
  useCurrentLocation,
} from "../components/LocationSelector";
import { BookedClientList } from "../components/reception/BookedClientList.jsx";
import { ReservationDialog } from "../components/reservationDialog/ReservationDialog";
import { NewAdhocDialog } from "../components/reservationDialog/NewAdhocDialog";
import { CheckedInClientList } from "../components/reception/CheckedInClientList.jsx";
import { TreatedClientList } from "../components/reception/TreatedClientList";
import HandoverAppointments from "../components/reception/HandoverAppointments.jsx";
import { status } from "shared/src/appointment.mjs";
import { groupBy, indexBy, throttle, updateIn } from "shared/src/util.mjs";
import { THROTTLE_REFRESH } from "../constants";
import { AppointmentDetailModal } from "../components/reception/AppointmentDetailModal";
import { ConfirmationModal } from "../components/reception/ConfirmationModal";

function enrichAppointments(appointments) {
  return updateIn(
    appointments,
    ["data", "appointments", "data"],
    function (appointments) {
      const appointmentById = indexBy(appointments, (a) => a.id);
      const childAppointments = groupBy(appointments, (a) => a.parent_id);
      delete childAppointments[null];

      for (const id in childAppointments) {
        const parent = appointmentById[id];
        if (parent == null) {
          continue;
        }
        parent.children = childAppointments[id];
      }

      return appointments;
    },
  );
}

export async function loader({ request }) {
  const location_id = currentLocationFromRequest(request);

  const now = new Date();
  const from = d.toString(d.startOfDay(now));
  const to = d.toString(d.endOfDay(now));

  const res = await api.loadAppointmentsWithNotes({
    filter: {
      start: { gte: from, lt: to },
      location_id: { eq: location_id },
    },
    order: {
      asc: "start",
    },
    location_id,
    nameDaysFilter: {
      month: { eq: now.getMonth() + 1 },
      day: { eq: now.getDate() },
    },
    locationSettingsFilter: {
      location_id: { eq: location_id },
      key: { eq: "reservation_settings" },
    },
  });

  return enrichAppointments(res);
}

/**
 * @param {object} msg
 * @param {string} location_id
 * @param {string} date
 *
 * @returns {boolean}
 */
function isInterestingNotification(msg, location_id, date) {
  switch (msg.type) {
    case "__connect":
      return true;
    case "appointment_changed": {
      const { location_ids, days } = msg.payload;

      if (Object.keys(msg.payload).length === 0) {
        return true;
      }

      return location_ids?.includes(location_id) && days.includes(date);
    }
  }

  return false;
}

function DetailModal({ appointments, appointment, close }) {
  const [activeDialog, setActiveDialog] = React.useState("detail");

  return (
    <>
      {activeDialog === "detail" && (
        <AppointmentDetailModal
          appointment={appointment}
          handleCloseAppointmentDetailModal={() => close()}
          handleOpenConfirmationModal={() => setActiveDialog("confirmation")}
        />
      )}
      {activeDialog === "confirmation" && (
        <ConfirmationModal
          appointments={appointments}
          appointment={appointment}
          handleCloseConfirmationModal={() => close()}
          newStatus={status.BOOKED}
        />
      )}
    </>
  );
}

export default function ReceptionDashboard() {
  const [showDetailModal, setShowDetailModal] = React.useState();
  const [maybeShowDetailModal, setMaybeShowDetailModal] = React.useState();
  const [ReservationDialogOptions, setReservationDialogOptions] =
    useState(null);
  const [newAdhocDialogOptions, setNewAdhocDialogOptions] = useState(null);
  const [handoverDialogOpen, setHandoverDialogOpen] = useState(false);
  const loaderData = useLoaderData();
  const appointments = loaderData.data.appointments.data;
  const nameDays = loaderData.data.nameDays.data;
  const order_from_h =
    loaderData.data.locationSettings.data?.reservation_settings?.order_from_h ||
    5;

  const location_id = useCurrentLocation();
  const revalidator = useRevalidator();
  const { t } = useTranslation();

  const [renderedAppointments, setRenderedAppointments] =
    useState(appointments);
  const [handoverAppointments, setHandoverAppointments] = useState([]);
  const [isHovered, setIsHovered] = useState(false);

  /** @type {StateTuple<string>} */
  const [hoverOverId, setHoverOverId] = useState("");
  const nameDayNames = useMemo(
    () => new Set(nameDays.map((d) => d.name)),
    nameDays,
  );

  useEffect(() => {
    const id = showDetailModal?.id;
    if (id == null) {
      if (maybeShowDetailModal != null) {
        const found = appointments.find((a) => a.id === maybeShowDetailModal);
        if (found != null) {
          setShowDetailModal(found);
          setMaybeShowDetailModal(null);
        }
      }

      return;
    }

    // refresh shown appointment details
    setShowDetailModal(appointments.find((a) => a.id === id));
  }, [appointments, showDetailModal, maybeShowDetailModal]);

  useEffect(() => {
    if (!isHovered) {
      const handoverApps = [];
      const renderedApps = [];

      for (const app of appointments) {
        const dur = app?.service?.locationService?.duration;
        const isHandover =
          dur === 0 &&
          [status.BOOKED, status.CONFIRMED, status.WAITING].includes(
            app.status_id,
          );

        if (isHandover) {
          handoverApps.push(app);
        } else {
          renderedApps.push(app);
        }
      }

      setHandoverAppointments(handoverApps);
      setRenderedAppointments(renderedApps);
    }
  }, [appointments, isHovered]);

  useEffect(() => {
    const refresh = throttle(() => {
      revalidator.revalidate();
    }, THROTTLE_REFRESH);

    function maybeRefresh(msg) {
      if (
        isInterestingNotification(msg, location_id, d.toDateString(new Date()))
      ) {
        refresh();
      }
    }

    ws.subscribe(maybeRefresh);

    return () => {
      ws.unsubscribe(maybeRefresh);
    };
  }, [location_id]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h1">{t("reception.dashboard")}</Typography>
        <Box
          sx={{
            display: "flex",
            gap: "2rem",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setHandoverDialogOpen(true)}
            disabled={handoverAppointments.length === 0}
            startIcon={<FontAwesomeIcon icon={faCalendarDays} />}
          >
            {t("reception.handover")} ({handoverAppointments.length})
          </Button>
        </Box>
      </Box>

      {handoverDialogOpen && (
        <HandoverAppointments
          open={handoverDialogOpen}
          handleClose={() => setHandoverDialogOpen(false)}
          appointments={handoverAppointments}
        />
      )}

      {ReservationDialogOptions && (
        <ReservationDialog {...ReservationDialogOptions} />
      )}

      {newAdhocDialogOptions && <NewAdhocDialog {...newAdhocDialogOptions} />}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 20%",
          gridTemplateAreas: `"booked treated" "checkedIn treated"`,
          width: "100%",
          gap: "2rem",
        }}
      >
        <Box sx={{ gridArea: "booked", gridRowStart: 1, gridRowEnd: 2 }}>
          <BookedClientList
            appointments={renderedAppointments}
            setIsHovered={setIsHovered}
            setNewAdhocDialogOptions={setNewAdhocDialogOptions}
            hoverOverId={hoverOverId}
            setHoverOverId={setHoverOverId}
            nameDayNames={nameDayNames}
            setShowDetailModal={setShowDetailModal}
            setMaybeShowDetailModal={setMaybeShowDetailModal}
          />
        </Box>
        <Box sx={{ gridArea: "checkedIn", gridRowStart: 2, gridRowEnd: 3 }}>
          <CheckedInClientList
            appointments={renderedAppointments}
            setIsHovered={setIsHovered}
            hoverOverId={hoverOverId}
            setHoverOverId={setHoverOverId}
            nameDayNames={nameDayNames}
            setShowDetailModal={setShowDetailModal}
          />
        </Box>
        <Box sx={{ gridArea: "treated", gridRowStart: 1, gridRowEnd: 4 }}>
          <TreatedClientList
            appointments={renderedAppointments}
            setReservationDialogOptions={setReservationDialogOptions}
            setIsHovered={setIsHovered}
            hoverOverId={hoverOverId}
            setHoverOverId={setHoverOverId}
            order_from_h={order_from_h}
          />
        </Box>
      </Box>
      {showDetailModal != null && (
        <DetailModal
          appointments={appointments}
          appointment={showDetailModal}
          close={() => setShowDetailModal(null)}
        />
      )}
    </>
  );
}
